
import React, { useState } from 'react';
import { X, Globe, Link as LinkIcon, Check, Copy, Share2, Lock, Eye, ToggleLeft, ToggleRight, Map as MapIcon, List, BookOpen } from 'lucide-react';
import { Language } from '../types';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  notebookId: string;
  notebookTitle: string;
  isPublic: boolean;
  onTogglePublic: (isPublic: boolean) => void;
  language: Language;
  onPreview: () => void;
  uid: string;
}

type LayoutPreference = 'map' | 'stream' | 'blog';

export const ShareModal: React.FC<ShareModalProps> = ({ 
  isOpen, onClose, notebookId, notebookTitle, isPublic, onTogglePublic, language = 'english', onPreview, uid
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<LayoutPreference>('map');
  
  // Link includes UID so the shared view knows WHICH user's notebook to load
  const baseUrl = window.location.href.split('?')[0];
  const shareLink = `${baseUrl}?mode=view&uid=${uid}&id=${notebookId}${selectedLayout !== 'map' ? `&layout=${selectedLayout}` : ''}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleOpenPreview = () => {
    // Before previewing, we might want to temporarily update the global layout state 
    // but App.tsx handlePreview does its own logic.
    onPreview();
    onClose();
  };

  if (!isOpen) return null;

  const labels = {
      english: {
          title: "Share to Web",
          desc: "Publish this mind map to the web. Anyone with the link can view it.",
          copy: "Copy Link",
          copied: "Copied!",
          preview: "Preview Public View",
          publicAccess: "Public Access",
          publicDesc: "Anyone with the link can view",
          privateAccess: "Private",
          privateDesc: "Only you can access this notebook",
          enablePublic: "Enable Public Access",
          footer: "You can monetize your frameworks in the future updates.",
          defaultView: "Default view for visitors",
          viewMap: "Map View",
          viewStream: "Stream View",
          viewBlog: "Blog View"
      },
      vietnamese: {
          title: "Chia sẻ công khai",
          desc: "Xuất bản mind map này lên web. Bất kỳ ai có link đều có thể xem.",
          copy: "Sao chép Link",
          copied: "Đã chép!",
          preview: "Xem thử giao diện khách",
          publicAccess: "Công khai",
          publicDesc: "Ai có link cũng xem được",
          privateAccess: "Riêng tư",
          privateDesc: "Chỉ bạn mới có thể xem",
          enablePublic: "Bật chia sẻ công khai",
          footer: "Bạn có thể kiếm tiền từ việc bán framework này trong các bản cập nhật sau.",
          defaultView: "Giao diện mặc định khi xem",
          viewMap: "Bản đồ",
          viewStream: "Dòng tin",
          viewBlog: "Bài viết"
      }
  };

  const t = labels[language];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-slate-800 w-full max-w-md rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-0 flex justify-between items-start">
           <div className="flex items-center gap-3">
               <div className={`p-2.5 rounded-xl ${isPublic ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'bg-gray-100 dark:bg-slate-800 text-gray-500'}`}>
                   <Share2 size={24} />
               </div>
               <div>
                   <h3 className="font-bold text-lg text-gray-900 dark:text-white leading-tight">{t.title}</h3>
                   <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{notebookTitle}</p>
               </div>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full text-gray-500 transition-colors">
               <X size={20} />
           </button>
        </div>

        <div className="p-6 space-y-6">
            <div 
                className={`p-4 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                    isPublic 
                    ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50' 
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-slate-600'
                }`}
                onClick={() => onTogglePublic(!isPublic)}
            >
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isPublic ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-gray-200 dark:bg-slate-800 text-gray-500'}`}>
                        {isPublic ? <Globe size={18} /> : <Lock size={18} />}
                    </div>
                    <div>
                        <div className="font-bold text-sm text-gray-800 dark:text-gray-200">
                            {isPublic ? t.publicAccess : t.privateAccess}
                        </div>
                        <div className="text-xs text-gray-500">
                            {isPublic ? t.publicDesc : t.privateDesc}
                        </div>
                    </div>
                </div>
                
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isPublic ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${isPublic ? 'translate-x-6' : 'translate-x-1'}`} />
                </div>
            </div>

            {/* Layout Preference Selector */}
            <div className={`space-y-2 transition-opacity duration-300 ${isPublic ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                 <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block ml-1">{t.defaultView}</label>
                 <div className="grid grid-cols-3 gap-2">
                    <button 
                        onClick={() => setSelectedLayout('map')}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${selectedLayout === 'map' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600' : 'border-gray-100 dark:border-slate-700 text-gray-400 hover:border-gray-200'}`}
                    >
                        <MapIcon size={18} />
                        <span className="text-[10px] font-bold">{t.viewMap}</span>
                    </button>
                    <button 
                        onClick={() => setSelectedLayout('stream')}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${selectedLayout === 'stream' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600' : 'border-gray-100 dark:border-slate-700 text-gray-400 hover:border-gray-200'}`}
                    >
                        <List size={18} />
                        <span className="text-[10px] font-bold">{t.viewStream}</span>
                    </button>
                    <button 
                        onClick={() => setSelectedLayout('blog')}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${selectedLayout === 'blog' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600' : 'border-gray-100 dark:border-slate-700 text-gray-400 hover:border-gray-200'}`}
                    >
                        <BookOpen size={18} />
                        <span className="text-[10px] font-bold">{t.viewBlog}</span>
                    </button>
                 </div>
            </div>

            <div className={`transition-opacity duration-300 ${isPublic ? 'opacity-100' : 'opacity-50 pointer-events-none grayscale'}`}>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block ml-1">Share Link</label>
                <div className="flex gap-2">
                    <div className="flex-1 bg-gray-100 dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm text-gray-600 dark:text-gray-300 truncate font-mono">
                        {isPublic ? shareLink : 'https://studyboard.app/link-hidden'}
                    </div>
                    <button 
                        onClick={handleCopy}
                        disabled={!isPublic}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition-colors flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCopied ? <Check size={16} /> : <Copy size={16} />}
                        {isCopied ? t.copied : t.copy}
                    </button>
                </div>
            </div>

            <button 
                onClick={handleOpenPreview}
                disabled={!isPublic}
                className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-xl text-gray-500 hover:text-blue-600 transition-all font-bold text-sm flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Eye size={18} className="group-hover:scale-110 transition-transform" />
                {t.preview}
            </button>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-slate-950 border-t dark:border-slate-800 text-center">
            <p className="text-[10px] text-gray-400 font-medium">
                {t.footer}
            </p>
        </div>
      </div>
    </div>
  );
};
